import React from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { HelpTooltip } from './HelpTooltip';

interface TimelineSettingsProps {
  implementationWeeks: number;
  adoptionRampMonths: number;
  earlyWinsEnabled: boolean;
  onImplementationWeeksChange: (weeks: number) => void;
  onAdoptionRampMonthsChange: (months: number) => void;
  onEarlyWinsToggle: (enabled: boolean) => void;
  disabled?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export const TimelineSettings: React.FC<TimelineSettingsProps> = ({
  implementationWeeks,
  adoptionRampMonths,
  earlyWinsEnabled,
  onImplementationWeeksChange,
  onAdoptionRampMonthsChange,
  onEarlyWinsToggle,
  disabled = false,
  isExpanded = false,
  onToggleExpand,
}) => {
  const implementationMonths = Math.ceil(implementationWeeks / 4.33);
  
  // Get complexity tier based on weeks
  // 1-2 months = 4-8.66 weeks (Standard)
  // 3-4 months = 13-17.33 weeks (Adaptive)  
  // 4-5 months = 17.33-21.66 weeks (Tailored)
  const getComplexityTier = (weeks: number) => {
    if (weeks <= 8) return { label: 'Standard', color: 'bg-green-100 text-green-700 border-green-200' };
    if (weeks > 8 && weeks < 17) return { label: 'Adaptive', color: 'bg-blue-100 text-blue-700 border-blue-200' };
    return { label: 'Tailored', color: 'bg-purple-100 text-purple-700 border-purple-200' };
  };
  
  const complexity = getComplexityTier(implementationWeeks);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header - Clickable */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-200 hover:from-indigo-100 hover:to-blue-100 transition-all duration-200"
        aria-expanded={isExpanded}
        aria-controls="timeline-settings-content"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-800">Timeline & Rollout</h3>
              <p className="text-xs text-gray-600">
                {isExpanded ? 'Configure your implementation timeline and adoption strategy' : `${implementationMonths}mo implementation, ${adoptionRampMonths}mo ramp, ${earlyWinsEnabled ? 'early wins enabled' : 'standard rollout'}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${complexity.color}`}>
              {complexity.label}
            </span>
            {/* Expand/Collapse Icon */}
            <svg 
              className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Collapsible Content */}
      <div
        id="timeline-settings-content"
        className={`transition-all duration-500 ease-out ${isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
      >
        <div className="p-6 space-y-8">
      
        {/* Implementation Duration */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <label className="text-sm font-semibold text-gray-900">
                Implementation Duration
              </label>
              <HelpTooltip
                title="Implementation Timeline"
                content="The time required to fully deploy and configure the scheduling platform across your organization. This includes technical setup, integrations, training, and initial rollout."
                details="Typical timelines: Standard (1-2 months) for straightforward deployments, Adaptive (3-4 months) for mid-size organizations with moderate customization, Tailored (4-5 months) for complex, highly customized deployments."
                position="bottom"
              />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-indigo-600">
                {implementationWeeks} <span className="text-sm font-normal text-gray-500">weeks</span>
              </div>
              <div className="text-xs text-gray-500">≈ {implementationMonths}mo</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="pb-5">
              <Slider
                min={4}
                max={20}
                value={implementationWeeks}
                onChange={(value) => onImplementationWeeksChange(value as number)}
                disabled={disabled}
                marks={{
                  4: '1mo',
                  8: '2mo',
                  12: '3mo',
                  16: '4mo',
                  20: '5mo',
                }}
                trackStyle={{ backgroundColor: '#0284c7', height: 6 }}
                handleStyle={{
                  borderColor: '#0284c7',
                  backgroundColor: '#0284c7',
                  width: 18,
                  height: 18,
                  marginTop: -6,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                railStyle={{ backgroundColor: '#E5E7EB', height: 6 }}
              />
            </div>
            
            {/* Quick presets */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onImplementationWeeksChange(6)}
                disabled={disabled}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  implementationWeeks <= 8 
                    ? 'bg-green-50 border-green-300 text-green-700' 
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => onImplementationWeeksChange(14)}
                disabled={disabled}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  implementationWeeks > 8 && implementationWeeks < 17 
                    ? 'bg-blue-50 border-blue-300 text-blue-700' 
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Adaptive
              </button>
              <button
                type="button"
                onClick={() => onImplementationWeeksChange(18)}
                disabled={disabled}
                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                  implementationWeeks >= 17 
                    ? 'bg-purple-50 border-purple-300 text-purple-700' 
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                Tailored
              </button>
            </div>
          </div>
        </div>

        {/* Adoption Ramp */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1">
              <label className="text-sm font-semibold text-gray-900">
                Adoption Ramp Period
              </label>
              <HelpTooltip
                title="User Adoption Timeline"
                content="The period after implementation when teams are learning and adapting to the new system. During this time, you'll realize approximately 50% of the full benefit."
                details="Set to 0 for immediate full adoption (rare), 1-3 months for typical rollout, or up to 12 months for complex enterprise deployments."
              />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-indigo-600">
                {adoptionRampMonths} <span className="text-sm font-normal text-gray-500">mo</span>
              </div>
              <div className="text-xs text-gray-500">at 50% benefit</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="pb-5">
              <Slider
                min={0}
                max={12}
                value={adoptionRampMonths}
                onChange={(value) => onAdoptionRampMonthsChange(value as number)}
                disabled={disabled}
                marks={{ 0: '0mo', 3: '3mo', 6: '6mo', 9: '9mo', 12: '12mo' }}
                trackStyle={{ backgroundColor: '#0284c7', height: 6 }}
                handleStyle={{
                  borderColor: '#0284c7',
                  backgroundColor: '#0284c7',
                  width: 18,
                  height: 18,
                  marginTop: -6,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
                railStyle={{ backgroundColor: '#E5E7EB', height: 6 }}
              />
            </div>
          </div>
        </div>

        {/* Early Wins Toggle */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <label className="text-sm font-semibold text-gray-900">
                  Progressive Early Wins
                </label>
                <HelpTooltip
                  title="Early Value Capture"
                  content="Enable progressive benefit capture during implementation (0-30% after first month). Benefits gradually increase as pilot programs expand, reaching 60% by end of adoption and 100% within 3 months after."
                  details="Best for phased rollouts or when you can implement scheduling improvements in parallel with technical setup."
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Start seeing returns before full deployment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={earlyWinsEnabled}
                onChange={(e) => onEarlyWinsToggle(e.target.checked)}
                disabled={disabled}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
          
          {earlyWinsEnabled && (
            <div className="mt-3 px-3 py-2 bg-white/70 rounded-lg">
              <p className="text-xs text-green-700">
                ✅ Progressive benefit capture (0-30%) after month 1 of implementation
              </p>
            </div>
          )}
        </div>

        {/* Timeline Visualization */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">Year 1 Value Realization Timeline</h4>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-400"></div>
                <span className="text-gray-600">Implementation</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-yellow-400"></div>
                <span className="text-gray-600">Adoption</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-green-500"></div>
                <span className="text-gray-600">Maturity</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex h-12 rounded-lg overflow-hidden shadow-inner bg-white">
              {/* Implementation Phase */}
              <div 
                className="bg-gradient-to-r from-red-400 to-red-500 flex items-center justify-center text-white font-semibold relative overflow-hidden"
                style={{ width: `${Math.max((implementationMonths / 12) * 100, 5)}%` }}
                title={earlyWinsEnabled ? 'Progressive ramp-up (0-30% after month 1)' : 'Setup Phase'}
              >
                <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                <span className={`relative z-10 px-1 ${(implementationMonths / 12) * 100 < 15 ? 'text-xs' : 'text-xs'}`}>
                  {(() => {
                    const widthPercent = (implementationMonths / 12) * 100;
                    const fullText = earlyWinsEnabled ? 'Ramp-up' : 'Setup Phase';
                    const shortText = earlyWinsEnabled ? 'Ramp' : 'Setup';
                    
                    if (widthPercent < 8) return '';
                    if (widthPercent < 15) return shortText;
                    return fullText;
                  })()}
                </span>
              </div>
              
              {/* Adoption Ramp */}
              {adoptionRampMonths > 0 && (
                <div 
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 flex items-center justify-center text-white font-semibold"
                  style={{ width: `${Math.max((adoptionRampMonths / 12) * 100, 5)}%` }}
                  title={earlyWinsEnabled ? "Progressive ramp from 30% to 60%" : "Progressive ramp from 0% to 50%"}
                >
                  <span className={`px-1 ${(adoptionRampMonths / 12) * 100 < 15 ? 'text-xs' : 'text-xs'}`}>
                    {(() => {
                      const widthPercent = (adoptionRampMonths / 12) * 100;
                      if (widthPercent < 8) return '';
                      if (widthPercent < 15) return 'Adopt';
                      return 'Adoption';
                    })()}
                  </span>
                </div>
              )}
              
              {/* Maturity Phase */}
              {12 - implementationMonths - adoptionRampMonths > 0 && (
                <div 
                  className="bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center text-white font-semibold"
                  style={{ width: `${Math.max(((12 - implementationMonths - adoptionRampMonths) / 12) * 100, 5)}%` }}
                  title="Ramp to 100% over 3 months post-adoption"
                >
                  <span className={`px-1 ${((12 - implementationMonths - adoptionRampMonths) / 12) * 100 < 15 ? 'text-xs' : 'text-xs'}`}>
                    {(() => {
                      const widthPercent = ((12 - implementationMonths - adoptionRampMonths) / 12) * 100;
                      if (widthPercent < 8) return '';
                      if (widthPercent < 15) return 'Ramp';
                      return 'Ramp to 100%';
                    })()}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex justify-between text-xs text-gray-500 font-medium">
              <span>1mo</span>
              <span>6mo</span>
              <span>12mo</span>
            </div>
          </div>
          
          {/* Summary Stats */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{implementationMonths}</div>
              <div className="text-xs text-gray-500">Setup Months</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{adoptionRampMonths}</div>
              <div className="text-xs text-gray-500">Adoption Months</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {Math.max(0, 12 - implementationMonths - adoptionRampMonths)}
              </div>
              <div className="text-xs text-gray-500">Full Months</div>
            </div>
          </div>
          
          {/* Extended timeline warning */}
          {implementationMonths + adoptionRampMonths > 12 && (
            <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 flex items-start gap-1">
                <span>⚠️</span>
                <span>Timeline extends beyond Year 1. Full benefits will begin in Month {implementationMonths + adoptionRampMonths + 1}.</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};